import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { EMPTY, catchError, filter, switchMap, tap } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TestCaseLinksService } from '../../../services/test-case-links.service';
import { CreateTestCaseLinkRequest, TestCaseLink, TestCaseLinkType } from '../../../models/test-case-link.model';
import { environment } from '../../../../environments/environment';

const TYPE_ICONS: Record<TestCaseLinkType, string> = {
  JIRA: 'shield',
  GITHUB: 'code',
  TESTCASE: 'check',
  REQUIREMENT: 'edit',
  URL: 'chevron_right',
};

const TYPE_MODIFIERS: Record<TestCaseLinkType, string> = {
  JIRA: 'jira',
  GITHUB: 'github',
  TESTCASE: 'testcase',
  REQUIREMENT: 'requirement',
  URL: 'url',
};

@Component({
  selector: 'app-test-case-links',
  imports: [TranslatePipe, MatIcon, MatProgressSpinner],
  templateUrl: './test-case-links.component.html',
  styleUrl: './test-case-links.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestCaseLinksComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly linksService = inject(TestCaseLinksService);

  readonly projectId = input.required<string>();
  readonly testCaseId = input<string | null>(null);

  readonly links = signal<TestCaseLink[]>([]);
  readonly loading = signal(false);
  readonly loadError = signal(false);
  readonly mutationError = signal(false);

  /**
   * Interne Draft-Repräsentation: koppelt jeden Draft an seine synthetische
   * ID (`draft-N`), damit `onRemove` per ID matcht statt per Inhalt. Ohne
   * diese Kopplung entfernte ein Klick bei Duplikat-Inhalten (gleicher Typ
   * + gleiche Referenz) beide Drafts aus dem Pending-State — die sichtbare
   * Zeile blieb im `links()`-Signal, wurde beim Save aber nicht angelegt
   * (WYSIWYG-Bruch, NEX-714-Review).
   */
  private readonly draftEntries = signal<{ draftId: string; request: CreateTestCaseLinkRequest }[]>([]);
  readonly draftLinks = computed(() => this.draftEntries().map((e) => e.request));
  readonly pendingDeletions = signal<string[]>([]);
  readonly draftLinksChange = output<CreateTestCaseLinkRequest[]>();
  readonly pendingDeletionsChange = output<string[]>();
  readonly openModalRequested = output<void>();

  readonly hasPendingChanges = computed(() => this.draftLinks().length > 0 || this.pendingDeletions().length > 0);
  private draftIdSeq = 0;

  constructor() {
    toObservable(this.testCaseId)
      .pipe(
        filter((id): id is string => !!id),
        tap(() => {
          this.loading.set(true);
          this.loadError.set(false);
        }),
        switchMap((id) =>
          this.linksService.list(this.projectId(), id).pipe(
            catchError(() => {
              this.loadError.set(true);
              this.loading.set(false);
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((links) => {
        this.links.set(links);
        this.loading.set(false);
      });
  }

  iconFor(type: TestCaseLinkType): string {
    return TYPE_ICONS[type];
  }

  modifierFor(type: TestCaseLinkType): string {
    return TYPE_MODIFIERS[type];
  }

  /** Externe Ziel-URL oder null, wenn der Typ keinen extern oeffenbaren Link darstellt. */
  externalUrl(link: TestCaseLink): string | null {
    switch (link.type) {
      case 'URL':
        return link.reference;
      case 'JIRA':
        return `${environment.jiraBrowseBase}${link.reference}`;
      case 'GITHUB':
        return this.gitHubUrl(link.reference);
      default:
        return null;
    }
  }

  /**
   * `owner/repo#42` wird zum Issue-/PR-Link aufgeloest (GitHub leitet `/issues/<n>` bei PRs
   * automatisch nach `/pull/<n>` um); reine `owner/repo`-Angaben bleiben Repo-Links.
   */
  private gitHubUrl(reference: string): string {
    if (reference.startsWith('http')) {
      return reference;
    }
    const issue = /^([^/]+\/[^/#]+)#(\d+)$/.exec(reference);
    if (issue) {
      return `https://github.com/${issue[1]}/issues/${issue[2]}`;
    }
    return `https://github.com/${reference}`;
  }

  requestAddModal(): void {
    this.openModalRequested.emit();
  }

  onAdd(request: CreateTestCaseLinkRequest): void {
    const draftId = `draft-${++this.draftIdSeq}`;
    const draft: TestCaseLink = {
      id: draftId,
      testCaseId: this.testCaseId() ?? '',
      type: request.type,
      reference: request.reference,
      displayLabel: request.displayLabel ?? null,
      createdAt: new Date().toISOString(),
    };
    this.links.update((current) => [...current, draft]);
    this.draftEntries.update((current) => [...current, { draftId, request }]);
    this.draftLinksChange.emit(this.draftLinks());
  }

  onRemove(link: TestCaseLink): void {
    this.links.update((current) => current.filter((l) => l.id !== link.id));
    if (link.id.startsWith('draft-')) {
      this.draftEntries.update((current) => current.filter((e) => e.draftId !== link.id));
      this.draftLinksChange.emit(this.draftLinks());
      return;
    }
    const next = [...this.pendingDeletions(), link.id];
    this.pendingDeletions.set(next);
    this.pendingDeletionsChange.emit(next);
  }

  resetPending(): void {
    this.draftEntries.set([]);
    this.pendingDeletions.set([]);
  }

  /**
   * Nach einem partiellen Save reconciled der Parent den Kind-State: erfolgreich
   * angelegte Drafts (Request-Referenz-Identisch aus dem Emit-Zyklus) und
   * erledigte Deletions werden entfernt. Ohne diese Reconciliation blieben die
   * bereits persistierten Drafts als `draft-N`-Zeilen sichtbar; der naechste
   * `draftLinksChange`-Emit ueberschriebe das Form-`pendingLinkDrafts` wieder
   * mit allen Drafts (inkl. der erfolgreichen) — der naechste Save legt sie
   * dann doppelt an (NEX-714 Review Runde 2, MZemelkaST).
   */
  applyPartialSuccess(successfulDrafts: CreateTestCaseLinkRequest[], successfulDeletionIds: string[]): void {
    if (successfulDrafts.length > 0) {
      const successSet = new Set(successfulDrafts);
      const entries = this.draftEntries();
      const removedDraftIds = new Set(entries.filter((e) => successSet.has(e.request)).map((e) => e.draftId));
      this.draftEntries.set(entries.filter((e) => !successSet.has(e.request)));
      this.links.update((current) => current.filter((l) => !removedDraftIds.has(l.id)));
      this.draftLinksChange.emit(this.draftLinks());
    }
    if (successfulDeletionIds.length > 0) {
      const doneSet = new Set(successfulDeletionIds);
      const next = this.pendingDeletions().filter((id) => !doneSet.has(id));
      this.pendingDeletions.set(next);
      this.pendingDeletionsChange.emit(next);
    }
  }
}
